import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useStateContext } from '@/contexts/ContextProvider';
import { useAdvanceOnboarding } from './api/useOnboarding';

export type OnboardingStepSlug =
    | 'password'
    | 'facility'
    | 'pms'
    | 'branding-logo'
    | 'branding-colors'
    | 'branding-fonts'
    | 'users'
    | 'first-flows'
    | 'recap';

const ALL_STEPS: OnboardingStepSlug[] = [
    'password',
    'facility',
    'pms',
    'branding-logo',
    'branding-colors',
    'branding-fonts',
    'users',
    'first-flows',
    'recap',
];

interface OnboardingContextValue {
    steps: OnboardingStepSlug[];
    currentStep: OnboardingStepSlug;
    currentIndex: number;
    isFirst: boolean;
    isLast: boolean;
    /**
     * Advance to the next step. The CALLER is responsible for saving step data BEFORE calling this.
     * This method only updates the backend step pointer and navigates the URL.
     */
    goNext: () => Promise<void>;
    goPrev: () => void;
    skipCurrent: () => Promise<void>;
    /**
     * Jump directly to any step by slug (used by the sidebar for back-navigation).
     * No save and no backend advance — pure URL change.
     */
    goToStep: (slug: OnboardingStepSlug) => void;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export function OnboardingProvider({ children }: { children: ReactNode }) {
    const navigate = useNavigate();
    const { step: paramStep } = useParams<{ step?: string }>();
    const { user } = useStateContext();
    const advanceMutation = useAdvanceOnboarding();

    const steps = useMemo<OnboardingStepSlug[]>(() => {
        if ((user as any)?.must_change_password) return ALL_STEPS;
        return ALL_STEPS.filter((s) => s !== 'password');
    }, [(user as any)?.must_change_password]);

    const currentStep: OnboardingStepSlug =
        (paramStep && (steps as string[]).includes(paramStep) ? paramStep : steps[0]) as OnboardingStepSlug;
    const currentIndex = steps.indexOf(currentStep);

    const goNext = async () => {
        const next = steps[currentIndex + 1];
        if (!next) return;
        await advanceMutation.mutateAsync(next);
        navigate(`/onboarding/${next}`);
    };

    const goPrev = () => {
        const prev = steps[currentIndex - 1];
        if (prev) navigate(`/onboarding/${prev}`);
    };

    const skipCurrent = async () => {
        await goNext();
    };

    const goToStep = (slug: OnboardingStepSlug) => {
        if ((steps as string[]).includes(slug)) {
            navigate(`/onboarding/${slug}`);
        }
    };

    const value: OnboardingContextValue = {
        steps,
        currentStep,
        currentIndex,
        isFirst: currentIndex === 0,
        isLast: currentIndex === steps.length - 1,
        goNext,
        goPrev,
        skipCurrent,
        goToStep,
    };

    return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

export function useOnboarding() {
    const ctx = useContext(OnboardingContext);
    if (!ctx) throw new Error('useOnboarding must be used inside <OnboardingProvider>');
    return ctx;
}
